<template>
  <article
    class="rung"
    :style="cardStyle"
    role="button"
    tabindex="0"
    @click="onOpen"
    @keydown.enter.prevent="onOpen"
    @keydown.space.prevent="onOpen"
  >
    <!-- capa do modulo, tingida com a cor do tier -->
    <div class="rung__cover">
      <img
        v-if="thumbUrl && !imgError"
        class="rung__img"
        :src="thumbUrl"
        :alt="course.title"
        loading="lazy"
        @error="imgError = true"
      />
      <span class="rung__veil" aria-hidden="true"></span>
    </div>

    <div class="rung__inner">
      <div class="rung__lead">
        <!-- plaqueta do nivel, carimbada sobre a capa -->
        <span class="rung__chip">
          <i v-if="isTraining" class="mdi mdi-school-outline rung__icon"></i>
          <span v-else class="rung__num">{{ numeral }}</span>
        </span>
        <div class="rung__titles">
          <p class="rung__eyebrow">
            <template v-if="isTraining">{{
              $t("course.trainingEyebrow")
            }}</template>
            <template v-else>{{
              $t("course.moduleEyebrow", { tier: meta.label, number: numeral })
            }}</template>
          </p>
          <h3 class="rung__title">{{ course.title }}</h3>
          <p class="rung__desc">{{ course.description }}</p>
        </div>
      </div>

      <div class="rung__foot">
        <!-- medidor de degraus: a posicao do modulo na escada -->
        <!-- Treinamento: no lugar do degrau, a barra de conclusão do
             professor (card 86akptec0). -->
        <div
          v-if="isTraining"
          class="tprogress"
          role="progressbar"
          :aria-valuenow="trainingPercent"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="$t('course.trainingAria', { percent: trainingPercent })"
        >
          <span
            class="tprogress__bar"
            :style="{ width: trainingPercent + '%' }"
          ></span>
        </div>
        <div
          v-else
          class="ladder"
          :aria-label="$t('course.ladderAria', { level: lvl })"
        >
          <span
            v-for="i in 11"
            :key="i"
            class="ladder__tick"
            :class="{ 'ladder__tick--on': isLeveled && i <= filled }"
          ></span>
        </div>

        <div class="rung__meta">
          <span class="rung__count">{{ countLabel }}</span>
          <button class="rung__go" type="button" @click.stop="onOpen">
            {{ ctaLabel }}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import {
  tierColor,
  tierLabel,
  isTrainingModule,
  TRAINING_COLOR,
} from "@/shared/courseTiers";

export default {
  name: "ModuleCard",

  props: {
    // Modulo do curso (title, description, level, thumb, classes/totalLesons)
    course: {
      type: Object,
      required: true,
    },
    // Contexto de quem ve: muda apenas o rotulo da acao
    role: {
      type: String,
      default: "student", // "student" | "teacher"
    },
  },

  data() {
    return {
      imgError: false,
    };
  },

  computed: {
    thumbUrl() {
      return this.course.thumb || null;
    },

    // Nivel como numero (o form entrega string; a API, number)
    lvl() {
      const n = Number(this.course.level);
      return Number.isFinite(n) ? n : 0;
    },

    // Os niveis 0-11 vivem na escada (tem medidor de degraus)
    isLeveled() {
      return this.lvl >= 0 && this.lvl <= 11;
    },

    // Quantos degraus acesos (1-11); o topo (Advanced II) acende todos
    filled() {
      return this.isLeveled ? Math.min(this.lvl + 1, 11) : 0;
    },

    // Nivel formatado com dois digitos (ex.: "02")
    numeral() {
      return String(this.lvl).padStart(2, "0");
    },

    isTraining() {
      return isTrainingModule(this.course);
    },

    // Aulas concluídas pelo professor no treinamento (vem mesclado pela tela
    // de materiais a partir de /trainings/progress).
    trainingDone() {
      return Number(this.course.completedCount) || 0;
    },

    trainingPercent() {
      const total = this.lessonsCount;
      if (!total) return 0;
      return Math.min(100, Math.round((this.trainingDone / total) * 100));
    },

    meta() {
      const level = this.lvl;
      if (this.isTraining) {
        return { label: this.$t("course.training"), color: TRAINING_COLOR };
      }
      return {
        label: tierLabel(level),
        color: tierColor(level),
      };
    },

    // Quantidade de aulas: aceita totalLesons (API) ou classes[] (relacao)
    lessonsCount() {
      if (typeof this.course.totalLesons === "number") {
        return this.course.totalLesons;
      }
      return Array.isArray(this.course.classes)
        ? this.course.classes.length
        : 0;
    },

    countLabel() {
      const count = this.lessonsCount;
      if (this.isTraining && count) {
        return this.$t("course.trainingCount", {
          done: this.trainingDone,
          total: count,
        });
      }
      return this.$tc("course.lessonsCountPlural", count, { count });
    },

    ctaLabel() {
      if (this.isTraining) {
        return this.trainingDone
          ? this.$t("common.continue")
          : this.$t("course.start");
      }
      return this.role === "teacher"
        ? this.$t("course.seeLessons")
        : this.$t("course.start");
    },

    cardStyle() {
      const c = this.meta.color;
      return {
        "--tier": c,
        "--tier-deep": this.shade(c, 0.28),
        "--tier-shadow": this.rgba(c, 0.42),
        "--tier-veil-top": this.rgba(c, 0.12),
        "--tier-veil-bot": this.rgba(c, 0.58),
      };
    },
  },

  methods: {
    onOpen() {
      this.$emit("open", this.course);
    },

    hexToRgb(hex) {
      const h = hex.replace("#", "");
      return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
      };
    },

    rgba(hex, alpha) {
      const { r, g, b } = this.hexToRgb(hex);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },

    // Escurece a cor em direcao ao preto (factor 0-1)
    shade(hex, factor) {
      const { r, g, b } = this.hexToRgb(hex);
      const f = 1 - factor;
      return `rgb(${Math.round(r * f)}, ${Math.round(g * f)}, ${Math.round(
        b * f
      )})`;
    },
  },
};
</script>

<style scoped>
.rung {
  --tier: var(--dk-grape, #9923cd);
  --tier-deep: var(--dk-grape-deep, #6e1994);
  --tier-shadow: rgba(153, 35, 205, 0.42);
  --tier-veil-top: rgba(153, 35, 205, 0.12);
  --tier-veil-bot: rgba(153, 35, 205, 0.58);
  --ink: var(--dk-ink, #211b2e);
  --ink-soft: var(--dk-ink-soft, #6e6a79);
  --ink-faint: var(--dk-faint, #9a96a6);
  --line: var(--dk-line, #eae7f0);
  --line-strong: var(--dk-line-strong, #ded9e8);

  position: relative;
  width: 100%;
  background: var(--dk-surface, #ffffff);
  border: 1px solid var(--line);
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  min-height: 300px;
  cursor: pointer;
  overflow: hidden;
  font-family: "Poppins", sans-serif;
  transition: transform 0.22s ease, box-shadow 0.22s ease,
    border-color 0.22s ease;
}
.rung:hover {
  transform: translateY(-4px);
  border-color: var(--line-strong);
  box-shadow: 0 26px 46px -30px var(--tier-shadow);
}
.rung:focus-visible {
  outline: 3px solid var(--tier);
  outline-offset: 2px;
}

/* capa */
.rung__cover {
  position: relative;
  height: 88px;
  flex: none;
  overflow: hidden;
  background: linear-gradient(135deg, var(--tier), var(--tier-deep));
}
.rung__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}
.rung:hover .rung__img {
  transform: scale(1.06);
}
.rung__veil {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    var(--tier-veil-top),
    var(--tier-veil-bot)
  );
}

/* conteudo */
.rung__inner {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  padding: 0 24px 22px;
}

.rung__lead {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-top: -34px;
}
/* plaqueta branca com o numeral, carimbada sobre a capa */
.rung__chip {
  flex: none;
  display: grid;
  place-items: center;
  width: 68px;
  height: 68px;
  border-radius: 16px;
  background: var(--dk-surface, #ffffff);
  box-shadow: 0 12px 26px -14px var(--tier-shadow);
}
.rung__num {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 2.2rem;
  line-height: 1;
  letter-spacing: -0.02em;
  color: var(--tier);
  font-variant-numeric: lining-nums;
}
.rung__icon {
  font-size: 2rem;
  line-height: 1;
  color: var(--tier);
}
.tprogress {
  height: 6px;
  margin: 4px 0 16px;
  border-radius: 999px;
  background: var(--line-strong);
  overflow: hidden;
}
.tprogress__bar {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--tier);
  transition: width 0.3s ease;
}
.rung__titles {
  min-width: 0;
  flex: 1 1 auto;
  padding-top: 40px;
}
.rung__eyebrow {
  margin: 0 0 6px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--tier);
}
.rung__title {
  margin: 0 0 5px;
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.28;
  color: var(--ink);
}
.rung__desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--ink-soft);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* rodape: medidor de degraus + meta + acao */
.rung__foot {
  position: relative;
  z-index: 1;
  margin-top: auto;
  padding-top: 20px;
}
.ladder {
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 14px;
  margin-bottom: 16px;
}
.ladder__tick {
  flex: 1;
  border-radius: 2px;
  background: var(--line-strong);
  transition: background 0.22s ease;
}
.ladder__tick--on {
  background: var(--tier);
}
/* degraus com altura crescente - a subida */
.ladder__tick:nth-child(1) {
  height: 36%;
}
.ladder__tick:nth-child(2) {
  height: 42%;
}
.ladder__tick:nth-child(3) {
  height: 48%;
}
.ladder__tick:nth-child(4) {
  height: 54%;
}
.ladder__tick:nth-child(5) {
  height: 60%;
}
.ladder__tick:nth-child(6) {
  height: 66%;
}
.ladder__tick:nth-child(7) {
  height: 72%;
}
.ladder__tick:nth-child(8) {
  height: 78%;
}
.ladder__tick:nth-child(9) {
  height: 85%;
}
.ladder__tick:nth-child(10) {
  height: 92%;
}
.ladder__tick:nth-child(11) {
  height: 100%;
}

.rung__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.rung__count {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--ink-faint);
  font-variant-numeric: tabular-nums;
}
.rung__go {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--tier);
  background: transparent;
  border: 0;
  padding: 6px 2px;
  cursor: pointer;
  transition: gap 0.18s ease;
}
.rung:hover .rung__go {
  gap: 11px;
}
.rung__go:focus-visible {
  outline: 2px solid var(--tier);
  outline-offset: 3px;
  border-radius: 4px;
}

@media (max-width: 520px) {
  .rung__num {
    font-size: 2rem;
  }
}
@media (prefers-reduced-motion: reduce) {
  .rung,
  .rung__img,
  .rung__num,
  .rung__go,
  .ladder__tick {
    transition: none;
  }
  .rung:hover {
    transform: none;
  }
  .rung:hover .rung__img {
    transform: none;
  }
}
</style>
